import { createClient } from '@supabase/supabase-js';
import { setViewerEmail } from './tracking.js'; // Import the new function

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Ensure URL has proper protocol and format
const fullSupabaseUrl = supabaseUrl.startsWith('http') ? supabaseUrl : `https://${supabaseUrl}`;
const supabase = createClient(fullSupabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true
  }
});

export class DocumentAuth {
  constructor() {
    this.docId = null;
    this.docMetadata = null;
    this.isVerified = false;
    this.passwordVerifiedPendingOTP = false;
    this.setupEventListeners();
  }

  setupEventListeners() {
    document.getElementById('password-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handlePasswordSubmit();
    });

    document.getElementById('email-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleEmailSubmit();
    });

    document.getElementById('otp-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleOTPSubmit();
    });
  }

  async checkDocumentAccess(docId) {
    this.docId = docId;
    
    try {
      // If already verified, skip the check
      if (this.isVerified && docId === this.docId) {
        return { authorized: true };
      }

      // Get document metadata
      const { data: doc, error } = await supabase
        .from('documents')
        .select('*')
        .eq('id', docId)
        .single();

      if (error) throw error;
      this.docMetadata = doc;

      // Check if document requires authentication
      if (!doc.password_hash && !doc.email_required) {
        return { authorized: true };
      }

      // If already fully verified, grant access
      if (this.isVerified) {
        return { authorized: true };
      }
      
      // Reset 2FA state if checking a new doc or verification isn't pending
      if (this.docId !== docId || !this.passwordVerifiedPendingOTP) {
        this.passwordVerifiedPendingOTP = false; 
      }

      // Show appropriate auth form based on requirements
      if (doc.password_hash) {
        // If OTP is also required AND password hasn't been verified yet, show password first
        if (doc.email_required && !this.passwordVerifiedPendingOTP) {
          document.getElementById('password-container').classList.remove('hidden');
          document.getElementById('viewer-container').classList.add('hidden');
          return { authorized: false, type: 'password_then_email' };
        } 
        // If only password is required, or if password was already verified (pending OTP), show password form
        else if (!doc.email_required) {
          document.getElementById('password-container').classList.remove('hidden');
          document.getElementById('viewer-container').classList.add('hidden');
          return { authorized: false, type: 'password' };
        }
      }

      // Show email form if email is required (and password isn't, or password was already verified)
      if (doc.email_required) {
        document.getElementById('email-container').classList.remove('hidden');
        document.getElementById('viewer-container').classList.add('hidden');
        return { authorized: false, type: 'email' }; // This includes the case where password was verified and now OTP is needed
      }
    } catch (error) {
      console.error('Error checking document access:', error);
      throw error;
    }
  }

  async handlePasswordSubmit() {
    const password = document.getElementById('password-input').value;
    const errorElement = document.getElementById('password-error');
    
    try {
      const { data, error } = await supabase.rpc('verify_document_password', {
        doc_id: this.docId,
        password_attempt: password
      });

      if (error) throw error;

      // The RPC returns an array with a single row
      if (data && data[0] && data[0].verified) {
        // Check if email is also required
        if (this.docMetadata.email_required) {
          this.passwordVerifiedPendingOTP = true;
          document.getElementById('password-container').classList.add('hidden');
          document.getElementById('email-container').classList.remove('hidden'); // Show email form next
        } else {
          // Only password was required, proceed to load document
          this.isVerified = true;  // Mark as fully verified
          document.getElementById('password-container').classList.add('hidden');
          document.getElementById('loading').classList.remove('hidden');
          window.dispatchEvent(new CustomEvent('passwordVerified', { detail: { docId: this.docId } }));
          this.recordAccess();
        }
      } else {
        errorElement.textContent = 'Incorrect password';
        errorElement.classList.remove('hidden');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      errorElement.textContent = 'Error verifying password';
      errorElement.classList.remove('hidden');
    }
  }

  async handleEmailSubmit() {
    const email = document.getElementById('email-input').value;
    const errorElement = document.getElementById('email-error');
    
    try {
      // Send OTP email using our serverless function
      const response = await fetch('/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          documentId: this.docId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send email');
      }

      // Show OTP input form
      document.getElementById('email-container').classList.add('hidden');
      document.getElementById('otp-container').classList.remove('hidden');
      document.getElementById('otp-email').textContent = email;
    } catch (error) {
      console.error('Error sending verification email:', error);
      errorElement.textContent = error.message || 'Error sending verification email';
      errorElement.classList.remove('hidden');
    }
  }

  async handleOTPSubmit() {
    const otp = document.getElementById('otp-input').value;
    const email = document.getElementById('otp-email').textContent;
    const errorElement = document.getElementById('otp-error');
    
    try {
      // Verify OTP using our serverless function
      const response = await fetch('/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          documentId: this.docId,
          otp: otp
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Invalid or expired code');
      }

      // If OTP is successful:
      this.isVerified = true; // Mark as fully verified
      setViewerEmail(email); // Store the verified email
      document.getElementById('otp-container').classList.add('hidden');
      document.getElementById('loading').classList.remove('hidden');
      
      // Ensure header and viewer container are visible
      document.querySelector('.site-header').classList.remove('hidden');
      document.getElementById('viewer-container').classList.remove('hidden');

      // Dispatch event to signal verification complete
      window.dispatchEvent(new CustomEvent('authVerified', { detail: { docId: this.docId, email: email } })); // Include email
      this.recordAccess();

    } catch (error) {
      console.error('Error verifying OTP:', error);
      errorElement.textContent = error.message || 'Error verifying code';
      errorElement.classList.remove('hidden');
    }
  }

  async recordAccess(documentAccessId = null) {
    // This method might be used for other non-analytics purposes in the future,
    // but we remove the direct analytics insert call as it's handled by tracking.js.
    console.log('Access recorded (auth module)', { docId: this.docId, accessId: documentAccessId });
    // try {
    //   await supabase
    //     .from('analytics_events')
    //     .insert({
    //       document_id: this.docId,
    //       document_access_id: documentAccessId,
    //       event_type: 'view',
    //       event_data: { initial_access: true },
    //       ip_address: null, // Will be set by RLS
    //       user_agent: navigator.userAgent
    //     });
    // } catch (error) {
    //   console.error('Error recording access:', error);
    // }
  }
} 